import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/detalhe/editar-item-sheet.tsx — Fase 12, Plano 31 (PV-07, T-12-46, T-12-48, T-12-49).
// FormDialog de edição de item AINDA NÃO RECEBIDO (pv:editar): PATCH /api/v1/vendas/pedidos/:id/itens/:itemId.
// Só os campos alterados são enviados; motivo obrigatório (auditoria); `versao` é a do item (lock otimista).
// Conflito de versão NÃO apaga o que foi digitado: os valores ficam até o usuário clicar em "Recarregar PV".
import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import type { EditarItemPvInput, PedidoVendaItemView, PedidoVendaView, TratamentoPecaDto } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { FormMessage } from '../../../components/ui/form-message';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { Textarea } from '../../../components/ui/textarea';
import { RoteiroCombobox } from '../../../components/vendas/roteiro-combobox';
import { motivoValido } from '../../../lib/vendas/ajustes';
import { focarCampo, ORDEM_ITEM_PV, primeiroCampoInvalido, validarItemPv, type CampoItemPv } from '../../../lib/vendas/validacao-pv';
import { lerNumeroDigitado, paraTexto, paraTextoPreco } from '../../../lib/vendas/numero';
import { usePvComandos } from '../use-pv-comandos';
import { AlertaConflitoPv } from './alerta-conflito-pv';

interface EditarItemSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pv: PedidoVendaView;
  /** Item em edição (versão atual vinda do PV carregado). */
  readonly item: PedidoVendaItemView | null;
  readonly onSalvo: (pv: PedidoVendaView) => void;
  readonly onRecarregar: () => Promise<PedidoVendaView | null>;
  readonly onFalha: (mensagem: string) => void;
}

interface Campos {
  codigo: string;
  descricao: string;
  cor: string;
  quantidade: string;
  unidade: string;
  roteiro: string | null;
  tratamentoPecaVersaoId: string | null;
  preco: string;
  previsao: string;
  motivo: string;
}

const ID_CAMPO: Record<CampoItemPv, string> = {
  codigoItemCliente: 'edit-item-codigo',
  descricao: 'edit-item-descricao',
  quantidade: 'edit-item-quantidade',
  unidade: 'edit-item-unidade',
  precoUnitario: 'edit-item-preco',
};

function camposDoItem(item: PedidoVendaItemView): Campos {
  return {
    codigo: item.codigoItemCliente,
    descricao: item.descricao,
    cor: item.cor ?? '',
    quantidade: paraTexto(item.quantidade),
    unidade: item.unidade,
    roteiro: item.roteiroId,
    tratamentoPecaVersaoId: item.tratamentoPecaVersaoId ?? null,
    preco: item.precoUnitario === null ? '' : paraTextoPreco(item.precoUnitario),
    previsao: item.previsaoEntrega ?? '',
    motivo: '',
  };
}

/** Só o que mudou em relação ao item (o servidor recusa "nada a alterar"); preço vazio limpa (null). */
export function diferencasDoItem(item: PedidoVendaItemView, campos: Campos): Omit<EditarItemPvInput, 'versao' | 'motivo'> {
  const corpo: Omit<EditarItemPvInput, 'versao' | 'motivo'> = {};
  if (campos.codigo.trim() !== item.codigoItemCliente) corpo.codigoItemCliente = campos.codigo.trim();
  if (campos.descricao.trim() !== item.descricao) corpo.descricao = campos.descricao.trim();
  if ((campos.cor.trim() || null) !== item.cor) corpo.cor = campos.cor.trim() || null;
  const quantidade = lerNumeroDigitado(campos.quantidade);
  if (Number.isFinite(quantidade) && quantidade !== item.quantidade) corpo.quantidade = quantidade;
  if (campos.unidade.trim() !== item.unidade) corpo.unidade = campos.unidade.trim();
  if (campos.roteiro !== item.roteiroId) corpo.roteiroId = campos.roteiro;
  if (campos.tratamentoPecaVersaoId !== (item.tratamentoPecaVersaoId ?? null)) corpo.tratamentoPecaVersaoId = campos.tratamentoPecaVersaoId;
  const preco = campos.preco.trim() === '' ? null : lerNumeroDigitado(campos.preco);
  if (preco !== item.precoUnitario && (preco === null || Number.isFinite(preco))) corpo.precoUnitario = preco;
  if ((campos.previsao || null) !== item.previsaoEntrega) corpo.previsaoEntrega = campos.previsao || null;
  return corpo;
}

export function EditarItemSheet({ aberto, onAbertoChange, pv, item, onSalvo, onRecarregar, onFalha }: EditarItemSheetProps) {
  const { editarItem } = usePvComandos();
  const [campos, setCampos] = useState<Campos | null>(item ? camposDoItem(item) : null);
  const [erros, setErros] = useState<Partial<Record<CampoItemPv | 'motivo' | 'nada', string>>>({});
  const [conflito, setConflito] = useState<string | null>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [tratamentos, setTratamentos] = useState<readonly TratamentoPecaDto[]>([]);
  const [recarregando, setRecarregando] = useState(false);
  const itemId = item?.id ?? null;

  // Reinicia só ao abrir (ou trocar de item): recarregar o PV por trás não pode apagar o que foi digitado.
  useEffect(() => {
    if (!aberto || !item) return;
    setCampos(camposDoItem(item));
    setErros({});
    setConflito(null);
    setErroEnvio(null);
  }, [aberto, itemId]);

  useEffect(() => {
    if (!aberto || !item?.materialId) { setTratamentos([]); return; }
    let ativo = true;
    const params = new URLSearchParams({ clienteId: pv.clienteId, materialId: item.materialId });
    if (item.tratamentoPecaVersaoId) params.set('versaoPreservadaId', item.tratamentoPecaVersaoId);
    void fetch(`/api/v1/vendas/lookups/tratamentos?${params.toString()}`, { credentials: 'include' })
      .then(async (res) => { if (ativo) setTratamentos(res.ok ? (await res.json()) as TratamentoPecaDto[] : []); })
      .catch(() => { if (ativo) setTratamentos([]); });
    return () => { ativo = false; };
  }, [aberto, item?.materialId, item?.tratamentoPecaVersaoId, pv.clienteId]);

  if (!item || !campos) return null;

  function alterar<K extends keyof Campos>(chave: K, valor: Campos[K]): void {
    setCampos((atuais) => (atuais ? { ...atuais, [chave]: valor } : atuais));
  }

  function selecionarTratamento(versaoId: string): void {
    const proximoId = versaoId || null;
    const versao = tratamentos.find((tratamento) => tratamento.vigente?.id === proximoId)?.vigente;
    setCampos((atuais) => atuais ? { ...atuais, tratamentoPecaVersaoId: proximoId, ...(versao ? { roteiro: versao.roteiroId, cor: versao.cor ?? '' } : {}) } : atuais);
  }

  async function salvar(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!item || !campos) return;
    setErroEnvio(null);
    const quantidade = lerNumeroDigitado(campos.quantidade);
    const preco = campos.preco.trim() === '' ? null : lerNumeroDigitado(campos.preco);
    const proximos: Partial<Record<CampoItemPv | 'motivo' | 'nada', string>> = validarItemPv({
      codigoItemCliente: campos.codigo,
      descricao: campos.descricao,
      quantidade,
      quantidadeTexto: campos.quantidade,
      unidade: campos.unidade,
      precoUnitario: preco,
      precoTexto: campos.preco,
    });
    if (!motivoValido(campos.motivo)) proximos.motivo = 'Informe o motivo.';
    const diferencas = diferencasDoItem(item, campos);
    if (Object.keys(diferencas).length === 0 && !proximos.motivo && Object.keys(proximos).length === 0) {
      proximos.nada = 'Altere ao menos um campo do item.';
    }
    setErros(proximos);
    const invalido = primeiroCampoInvalido(proximos, ORDEM_ITEM_PV);
    if (invalido) {
      focarCampo(ID_CAMPO[invalido]);
      return;
    }
    if (proximos.motivo) {
      focarCampo('edit-item-motivo');
      return;
    }
    if (proximos.nada) return;

    setSalvando(true);
    try {
      const resultado = await editarItem(pv.id, item.id, { ...diferencas, versao: item.versao, motivo: campos.motivo.trim() });
      if (resultado.tipo === 'ok') {
        setConflito(null);
        onSalvo(resultado.resultado);
        onAbertoChange(false);
      } else if (resultado.tipo === 'conflito') {
        setConflito(resultado.message);
      } else {
        setErroEnvio(resultado.message);
        onFalha(resultado.message);
      }
    } finally {
      setSalvando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      const atual = await onRecarregar();
      const itemAtual = atual?.itens.find((candidato) => candidato.id === itemId);
      if (itemAtual) {
        setCampos(camposDoItem(itemAtual));
        setErros({});
        setConflito(null);
        setErroEnvio(null);
      } else {
        setErroEnvio('Não foi possível recarregar o PV. Tente novamente.');
      }
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={(e) => void salvar(e)} noValidate className="flex h-full flex-col">
          <FormDialogHeader>
            <FormDialogTitle className="text-xl">Editar item {item.numeroItem}</FormDialogTitle>
            <FormDialogDescription>Item ainda não recebido. Toda alteração fica registrada com o motivo.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-codigo">Código do item do cliente</Label>
              <Input
                id="edit-item-codigo"
                value={campos.codigo}
                onChange={(e) => alterar('codigo', e.target.value)}
                aria-invalid={erros.codigoItemCliente ? true : undefined}
                autoFocus
              />
              {erros.codigoItemCliente && <FormMessage>{erros.codigoItemCliente}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-descricao">Descrição</Label>
              <Input
                id="edit-item-descricao"
                value={campos.descricao}
                onChange={(e) => alterar('descricao', e.target.value)}
                aria-invalid={erros.descricao ? true : undefined}
              />
              {erros.descricao && <FormMessage>{erros.descricao}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-cor">Cor</Label>
              <Input id="edit-item-cor" value={campos.cor} disabled={campos.tratamentoPecaVersaoId !== null} onChange={(e) => alterar('cor', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-item-quantidade">Quantidade</Label>
                <Input
                  id="edit-item-quantidade"
                  inputMode="decimal"
                  value={campos.quantidade}
                  onChange={(e) => alterar('quantidade', e.target.value)}
                  className="text-right tabular-nums"
                  aria-invalid={erros.quantidade ? true : undefined}
                />
                {erros.quantidade && <FormMessage>{erros.quantidade}</FormMessage>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-item-unidade">Unidade</Label>
                <Input
                  id="edit-item-unidade"
                  value={campos.unidade}
                  onChange={(e) => alterar('unidade', e.target.value)}
                  aria-invalid={erros.unidade ? true : undefined}
                />
                {erros.unidade && <FormMessage>{erros.unidade}</FormMessage>}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-roteiro">Processo / roteiro</Label>
              <RoteiroCombobox id="edit-item-roteiro" value={campos.roteiro} disabled={campos.tratamentoPecaVersaoId !== null} onChange={(roteiro) => alterar('roteiro', roteiro)} />
            </div>
            {tratamentos.length > 0 ? <div className="flex flex-col gap-1.5"><Label htmlFor="edit-item-tratamento">Tratamento da peça</Label><CampoSelect id="edit-item-tratamento" value={campos.tratamentoPecaVersaoId ?? ''} onValueChange={selecionarTratamento} placeholder="Selecione o tratamento aprovado" options={tratamentos.flatMap((tratamento) => tratamento.vigente ? [{ value: tratamento.vigente.id, label: `${tratamento.codigo} · ${tratamento.nome} · v${tratamento.vigente.versao}${tratamento.vigente.cor ? ` · ${tratamento.vigente.cor}` : ''}` }] : [])} /><p className="text-xs text-muted-foreground">A seleção fixa roteiro e cor da versão escolhida.</p></div> : null}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-preco">Preço unitário</Label>
              <Input
                id="edit-item-preco"
                inputMode="decimal"
                value={campos.preco}
                onChange={(e) => alterar('preco', e.target.value)}
                className="text-right tabular-nums"
                aria-invalid={erros.precoUnitario ? true : undefined}
              />
              {erros.precoUnitario && <FormMessage>{erros.precoUnitario}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-previsao">Previsão de entrega</Label>
              <DatePicker id="edit-item-previsao" value={campos.previsao} onValueChange={(value) => alterar('previsao', value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-item-motivo">Motivo do ajuste (obrigatório)</Label>
              <Textarea
                id="edit-item-motivo"
                value={campos.motivo}
                onChange={(e) => alterar('motivo', e.target.value)}
                aria-invalid={erros.motivo ? true : undefined}
              />
              {erros.motivo && <FormMessage>{erros.motivo}</FormMessage>}
            </div>
            {erros.nada && <FormMessage>{erros.nada}</FormMessage>}
            {erroEnvio && <FormMessage>{erroEnvio}</FormMessage>}
          </div>

          <FormDialogFooter className="sm:flex-row sm:justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={salvando}>
              Voltar
            </Button>
            <Button type="submit" disabled={salvando}>
              {salvando && <Loader2 className="animate-spin" aria-hidden="true" />}
              Salvar item
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
